import { DateTime } from 'luxon';

export const GUEST_MAX_CONCURRENT_EVENTS = 3;
export const GUEST_CAPACITY_FULL_EVENT_PREFIX = 'guest-capacity-full';
export const GUEST_CAPACITY_SLOT_MINUTES = 30;

const DEFAULT_TIMEZONE = 'Europe/Paris';
const GUEST_WORK_START_HOUR = 10;
const GUEST_WORK_END_HOUR = 19;

function normalizeComparableName(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

function toDateTime(value, timezone = DEFAULT_TIMEZONE) {
  if (!value) {
    return null;
  }

  if (DateTime.isDateTime(value)) {
    return value.setZone(timezone);
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value).setZone(timezone);
  }

  if (typeof value === 'string') {
    const parsed = DateTime.fromISO(value, { zone: timezone });
    return parsed.isValid ? parsed : null;
  }

  return null;
}

function getEventStartMillis(event, timezone = DEFAULT_TIMEZONE) {
  const start = event?.start || event?.startStr || event?.start_time;
  const startDateTime = toDateTime(start, timezone);
  return startDateTime?.isValid ? startDateTime.toMillis() : null;
}

function getEventEndMillis(event, timezone = DEFAULT_TIMEZONE) {
  const fallbackStart = getEventStartMillis(event, timezone);
  const end = event?.end || event?.endStr || event?.end_time;
  const endDateTime = toDateTime(end, timezone);

  if (endDateTime?.isValid) {
    return endDateTime.toMillis();
  }

  return fallbackStart === null ? null : fallbackStart + 60 * 60 * 1000;
}

function getEventInterval(event, timezone = DEFAULT_TIMEZONE) {
  const start = getEventStartMillis(event, timezone);
  const end = getEventEndMillis(event, timezone);

  if (start === null || end === null) {
    return null;
  }

  return { event, start, end };
}

function intervalOverlapsWindow(interval, windowStartMillis, windowEndMillis) {
  return interval.start < windowEndMillis && interval.end > windowStartMillis;
}

function countMaxConcurrentInWindow(intervals, windowStartMillis, windowEndMillis) {
  const points = [];

  intervals.forEach((interval) => {
    const start = Math.max(interval.start, windowStartMillis);
    const end = Math.min(interval.end, windowEndMillis);

    if (start < end) {
      points.push({ time: start, delta: 1 });
      points.push({ time: end, delta: -1 });
    }
  });

  points.sort((left, right) => {
    if (left.time !== right.time) {
      return left.time - right.time;
    }

    return left.delta - right.delta;
  });

  let currentCount = 0;
  let maxCount = 0;

  points.forEach((point) => {
    currentCount += point.delta;
    maxCount = Math.max(maxCount, currentCount);
  });

  return maxCount;
}

function getCalendarType(event) {
  return event?.extendedProps?.calendarType
    || event?.extendedProps?.calendar_type
    || event?.calendar_type
    || null;
}

function getCreatedBy(event) {
  return event?.extendedProps?.createdBy
    || event?.extendedProps?.created_by
    || event?.created_by
    || event?.user_id
    || null;
}

export function isBidonEventExemptForGuest(event, guestName) {
  const normalizedGuestName = normalizeComparableName(guestName);

  if (!normalizedGuestName || !event) {
    return false;
  }

  const calendarType = getCalendarType(event);

  if (calendarType !== 'bidon' && event.extendedProps?.isBidonEvent !== true) {
    return false;
  }

  const originalTitle = event.extendedProps?.originalTitle || event.title || '';
  return normalizeComparableName(originalTitle) === normalizedGuestName;
}

export function shouldCountGuestConcurrentEvent(event, guestName, excludeEventId = null) {
  if (!event) {
    return false;
  }

  if (excludeEventId !== null && excludeEventId !== undefined && String(event.id) === String(excludeEventId)) {
    return false;
  }

  if (event.allDay) {
    return false;
  }

  if (event.extendedProps?.isCapacityFull || event.extendedProps?.isGuestBlockedDay) {
    return false;
  }

  if (isBidonEventExemptForGuest(event, guestName)) {
    return false;
  }

  return true;
}

export function isGuestCapacityFullEvent(event) {
  if (!event) {
    return false;
  }

  return event.extendedProps?.isCapacityFull === true
    || String(event.id || '').startsWith(`${GUEST_CAPACITY_FULL_EVENT_PREFIX}-`);
}

export function isOwnGuestCalendarEvent(event, currentUserId = null) {
  if (!event || event.allDay) {
    return false;
  }

  const createdBy = getCreatedBy(event);
  if (
    currentUserId !== null
    && currentUserId !== undefined
    && createdBy !== null
    && createdBy !== undefined
  ) {
    return String(createdBy) === String(currentUserId);
  }

  return event.extendedProps?.isGuestEvent === true || getCalendarType(event) === 'guest';
}

export function hasOwnGuestEventOverlap({
  events,
  startTime,
  endTime,
  currentUserId,
  excludeEventId = null,
  timezone = DEFAULT_TIMEZONE
}) {
  const startDateTime = toDateTime(startTime, timezone);
  const endDateTime = toDateTime(endTime, timezone);

  if (!startDateTime?.isValid || !endDateTime?.isValid || endDateTime <= startDateTime) {
    return false;
  }

  const windowStartMillis = startDateTime.toMillis();
  const windowEndMillis = endDateTime.toMillis();

  return (events || []).some((event) => {
    if (!isOwnGuestCalendarEvent(event, currentUserId)) {
      return false;
    }

    if (
      excludeEventId !== null
      && excludeEventId !== undefined
      && String(event.id) === String(excludeEventId)
    ) {
      return false;
    }

    const interval = getEventInterval(event, timezone);
    return interval ? intervalOverlapsWindow(interval, windowStartMillis, windowEndMillis) : false;
  });
}

async function loadLimitForDateTime(dateTime, {
  loadEffectiveLimit,
  fallbackLimit,
  limitCache
}) {
  const dateKey = dateTime.toFormat('yyyy-MM-dd');
  const hour = dateTime.hour;
  const cacheKey = `${dateKey}-${hour}`;

  if (limitCache.has(cacheKey)) {
    return limitCache.get(cacheKey);
  }

  let limitInfo = {
    limit: fallbackLimit,
    source: 'fallback',
    description: null
  };

  if (typeof loadEffectiveLimit === 'function') {
    try {
      const result = await loadEffectiveLimit(dateKey, hour);
      const limit = Number(result?.limit_count);

      if (result?.success && Number.isFinite(limit)) {
        limitInfo = {
          limit,
          source: result.source || 'effective-limit',
          description: result.description || null
        };
      }
    } catch (error) {
      limitInfo = {
        limit: fallbackLimit,
        source: 'fallback',
        description: null
      };
    }
  }

  limitCache.set(cacheKey, limitInfo);
  return limitInfo;
}

export async function findGuestCapacityConflict({
  startTime,
  endTime,
  events,
  guestName,
  excludeEventId = null,
  loadEffectiveLimit,
  fallbackLimit = GUEST_MAX_CONCURRENT_EVENTS,
  timezone = DEFAULT_TIMEZONE,
  limitCache = new Map(),
  stepMinutes = GUEST_CAPACITY_SLOT_MINUTES
}) {
  const startDateTime = toDateTime(startTime, timezone);
  const endDateTime = toDateTime(endTime, timezone);

  if (!startDateTime?.isValid || !endDateTime?.isValid || endDateTime <= startDateTime) {
    return null;
  }

  const countableIntervals = (events || [])
    .filter((event) => shouldCountGuestConcurrentEvent(event, guestName, excludeEventId))
    .map((event) => getEventInterval(event, timezone))
    .filter(Boolean);
  const scanMinutes = Math.max(1, Number(stepMinutes) || GUEST_CAPACITY_SLOT_MINUTES);

  for (
    let current = startDateTime;
    current < endDateTime;
    current = current.plus({ minutes: scanMinutes })
  ) {
    const currentStart = current.toMillis();
    const currentEnd = Math.min(current.plus({ minutes: scanMinutes }).toMillis(), endDateTime.toMillis());
    const limitInfo = await loadLimitForDateTime(current, {
      loadEffectiveLimit,
      fallbackLimit,
      limitCache
    });

    const overlapCount = countMaxConcurrentInWindow(countableIntervals, currentStart, currentEnd);

    if (overlapCount >= limitInfo.limit) {
      return {
        ...limitInfo,
        currentCount: overlapCount,
        time: current.toISO()
      };
    }
  }

  return null;
}

export async function buildGuestCapacityFullEvents({
  viewStart,
  viewEnd,
  events,
  guestName,
  currentUserId = null,
  loadEffectiveLimit,
  fallbackLimit = GUEST_MAX_CONCURRENT_EVENTS,
  timezone = DEFAULT_TIMEZONE
}) {
  const viewStartDateTime = toDateTime(viewStart, timezone);
  const viewEndDateTime = toDateTime(viewEnd, timezone);

  if (!viewStartDateTime?.isValid || !viewEndDateTime?.isValid || viewEndDateTime <= viewStartDateTime) {
    return [];
  }

  const sourceEvents = events || [];
  const countableIntervals = sourceEvents
    .filter((event) => shouldCountGuestConcurrentEvent(event, guestName))
    .map((event) => getEventInterval(event, timezone))
    .filter(Boolean);
  const ownEventIntervals = sourceEvents
    .filter((event) => isOwnGuestCalendarEvent(event, currentUserId))
    .map((event) => getEventInterval(event, timezone))
    .filter(Boolean);
  const limitCache = new Map();
  const capacityEvents = [];

  for (
    let day = viewStartDateTime.startOf('day');
    day < viewEndDateTime;
    day = day.plus({ days: 1 })
  ) {
    for (let hour = GUEST_WORK_START_HOUR; hour < GUEST_WORK_END_HOUR; hour += 1) {
      for (const minute of [0, GUEST_CAPACITY_SLOT_MINUTES]) {
        const slotStart = day.set({ hour, minute, second: 0, millisecond: 0 });
        const slotEnd = slotStart.plus({ minutes: GUEST_CAPACITY_SLOT_MINUTES });

        if (slotEnd <= viewStartDateTime || slotStart >= viewEndDateTime) {
          continue;
        }

        const slotStartMillis = slotStart.toMillis();
        const slotEndMillis = slotEnd.toMillis();
        const hasOwnEvent = ownEventIntervals.some((interval) =>
          intervalOverlapsWindow(interval, slotStartMillis, slotEndMillis)
        );

        if (hasOwnEvent) {
          continue;
        }

        const limitInfo = await loadLimitForDateTime(slotStart, {
          loadEffectiveLimit,
          fallbackLimit,
          limitCache
        });
        const overlapCount = countMaxConcurrentInWindow(countableIntervals, slotStartMillis, slotEndMillis);

        if (overlapCount < limitInfo.limit) {
          continue;
        }

        const slotKey = slotStart.toFormat("yyyyMMdd'T'HHmm");
        capacityEvents.push({
          id: `${GUEST_CAPACITY_FULL_EVENT_PREFIX}-${slotKey}`,
          title: '',
          start: slotStart.toISO(),
          end: slotEnd.toISO(),
          display: 'background',
          editable: false,
          classNames: ['guest-capacity-full-bg-event'],
          backgroundColor: 'rgba(220, 38, 38, 0.18)',
          borderColor: 'rgba(220, 38, 38, 0.28)',
          extendedProps: {
            isBackground: true,
            isCapacityFull: true,
            limit: limitInfo.limit,
            currentCount: overlapCount,
            source: limitInfo.source
          }
        });
      }
    }
  }

  return capacityEvents;
}
